import * as ActionTypes from "../actions/types";
import {
  mapOrganizationContent,
  mapOrganizationData,
  mapOrganizationMember,
  mapOrganizationMembers,
  mapOrganizationRoles,
  mapStatusMessage,
} from "../constants/map/organization";
import {
  ERROR_CREATE_MEMBERSHIP,
  ERROR_RESEND_INVITATION,
  SUCCESS_RESEND_INVITATION,
} from "../constants/organizationRoles";
import { parseOrganizations } from "../constants/parse/organization";

const INITIAL_STATE = {
  byId: {},
  rolesById: {},
  loading: false,
  loadingOrganizationContent: false,
  removingMembership: {},
  messages: {
    success: {},
    fail: {},
  },
};

function fetchOrganizations(state) {
  state.byId = {};
  state.loading = true;
}

function storeOrganizations(state, action) {
  let { payload } = action;

  // Handle new API format
  if (payload && payload.payload && payload.payload.data) {
    const mappedData = mapOrganizationData(payload);
    let allOrganizations = {
      ...state.byId,
      ...mappedData,
    };

    // Check if there's pagination metadata
    const metadata = payload.payload.metadata;
    let loading = false;

    if (metadata) {
      const { pageOffset, pageLimit, total } = metadata;
      // Check if there are more pages to load
      loading = pageOffset + pageLimit < total;
    }

    state.loading = loading;
    state.byId = allOrganizations;
    return;
  }

  // Handle old format (for backward compatibility)
  if (payload.data && !payload.data.length && payload.data.length !== 0) {
    //when requesting single organization
    payload = {
      ...payload,
      data: [payload.data],
    };
  }
  let loading = false;
  const mappedData = mapOrganizationData(payload);
  let allOrganizations = {
    ...state.byId,
    ...mappedData,
  };

  // Check if we are on the last page and rearrange the array in order to
  // show our organization first
  if (payload.links && payload.links.next) {
    loading = true;
  } else {
    if (payload.uid) {
      const updatedOrganizations = {
        ...state.byId,
        ...mappedData,
      };
      const myOrganizations = Object.values(updatedOrganizations).filter(
        (element) => element.uid === payload.uid
      );
      const NotMyOrganizations = Object.values(updatedOrganizations).filter(
        (element) => element.uid !== payload.uid
      );
      allOrganizations = [...myOrganizations, ...NotMyOrganizations];
    }
    loading = false;
  }
  state.loading = loading;
  state.byId = allOrganizations;
}

function fetchOrganizationsFailed(state, action) {
  state.loading = false;
}

function fetchOrganizationMemberships(state) {
  state.loading = true;
}

function fetchOrganizationMembershipsFailed(state) {
  state.loading = false;
}

function fetchOrganizationRoles(state, action) {
  state.loading = true;
}

function fetchOrganizationRolesSuccess(state, action) {
  const data = action.payload;
  state.loading = false;
  state.rolesById = {
    ...state.rolesById,
    ...mapOrganizationRoles(data),
  };
}

function fetchOrganizationRolesFailed(state, action) {
  state.loading = false;
}

function updateOrganizationMembership(state, action) {
  state.loading = true;
}

function updateOrganizationMembershipFailed(state, action) {
  state.loading = false;
}

function fetchOrganizationContent(state, action) {
  state.loadingOrganizationContent = true;
}

function fetchOrganizationContentSuccess(state, action) {
  const { data } = action.payload;
  const organizationUuid =
    data && data[0] ? data[0].relationships.gid.data.id : null;

  if (!organizationUuid) {
    const organizationsById = state.byId;
    const keys = Object.keys(organizationsById);
    keys.forEach((id) => {
      if (!state.byId[id]) state.byId[id] = {};
      state.byId[id].content = {};
    });
    state.loadingOrganizationContent = false;
    return;
  }
  if (!state.byId[organizationUuid]) state.byId[organizationUuid] = {};
  state.byId[organizationUuid].content = mapOrganizationContent(action.payload);
  state.loadingOrganizationContent = false;
}

function fetchOrganizationContentFailed(state, action) {
  state.loadingOrganizationContent = false;
}

function createOrganizationContent(state, action) {
  state.loading = true;
}

function createOrganizationContentSuccess(state, action) {
  state.loading = false;
}

function createOrganizationContentFailed(state, action) {
  state.loading = false;
}

function createOrganizationMembership(state, action) {
  state.loading = true;
}

function createOrganizationMembershipFailed(state, action) {
  const error = mapStatusMessage(action.payload, ERROR_CREATE_MEMBERSHIP);
  if (!state.messages) state.messages = { success: {}, fail: {} };
  if (!state.messages.fail) state.messages.fail = {};
  state.messages.fail[error.uuid] = error;
  state.loading = false;
}

function storeOrganizationMemberships(state, action) {
  const { data, included } = action.payload;
  const organizationUuid =
    data && data[0] ? data[0].relationships.gid.data.id : null;
  if (!organizationUuid) {
    state.loading = false;
    return;
  }
  const roles = included
    ? included.filter((x) => x.type === "group_role--group_role")
    : [];
  if (!state.byId[organizationUuid]) state.byId[organizationUuid] = {};
  state.byId[organizationUuid].members = mapOrganizationMembers(action.payload);
  state.rolesById = {
    ...state.rolesById,
    ...mapOrganizationRoles({
      data: roles,
      included: included,
    }),
  };
  state.loading = false;
}

function storeOrganizationMembership(state, action) {
  const { data, included } = action.payload;
  const organizationUuid = data ? data.relationships.gid.data.id : null;
  if (!organizationUuid) {
    state.loading = false;
    return;
  }
  const member = mapOrganizationMember(data, included);
  if (!state.byId[organizationUuid]) state.byId[organizationUuid] = {};
  if (!state.byId[organizationUuid].members)
    state.byId[organizationUuid].members = {};
  state.byId[organizationUuid].members[member.membershipUuid] = member;
  state.loading = false;
}

function deleteOrganizationMembership(state, action) {
  const { organizationUuid, membershipUuid } = action.payload;
  if (!state.removingMembership) state.removingMembership = {};
  state.removingMembership[membershipUuid] = organizationUuid;
}

function deleteOrganizationMembershipSuccess(state, action) {
  const { membershipUuid } = action.payload;
  const organizationUuid = state.removingMembership?.[membershipUuid];
  if (state.removingMembership) {
    delete state.removingMembership[membershipUuid];
  }
  if (organizationUuid && state.byId[organizationUuid]?.members) {
    delete state.byId[organizationUuid].members[membershipUuid];
  }
  state.loading = false;
}

function deleteOrganizationMembershipFailed(state, action) {
  const { membershipUuid } = action.payload;
  if (state.removingMembership) {
    delete state.removingMembership[membershipUuid];
  }
  state.loading = false;
}

function removeOrganizationStatusMessage(state, action) {
  const { uuid, type } = action.payload; //success, error
  if (state.messages?.[type]) {
    delete state.messages[type][uuid];
  }
}

function deleteOrganization(state, action) {
  state.loading = true;
}

function deleteOrganizationSuccess(state, action) {
  const { uuid } = action.payload;
  if (state.byId) {
    delete state.byId[uuid];
  }
  state.loading = false;
}

function deleteOrganizationFailed(state, action) {
  state.loading = false;
}

function resendMemberInvitation(state, action) {
  state.loading = true;
}

function resendMemberInvitationSuccess(state, action) {
  const successMessage = action.payload;
  const success = mapStatusMessage(successMessage, SUCCESS_RESEND_INVITATION);
  if (!state.messages) state.messages = { success: {}, fail: {} };
  if (!state.messages.success) state.messages.success = {};
  state.messages.success[success.uuid] = success;
  state.loading = false;
}

function resendMemberInvitationFailed(state, action) {
  const { response } = action.payload;
  const errorMessage =
    response && response.text
      ? response.text
      : "Something went wrong try again later.";
  const error = mapStatusMessage(errorMessage, ERROR_RESEND_INVITATION);
  if (!state.messages) state.messages = { success: {}, fail: {} };
  if (!state.messages.fail) state.messages.fail = {};
  state.messages.fail[error.uuid] = error;
  state.loading = false;
}

export const organizationReducer = {
  initialState: INITIAL_STATE,
  handlers: {
    [ActionTypes.FETCH_ORGANIZATIONS]: fetchOrganizations,
    [ActionTypes.FETCH_ORGANIZATIONS_FAILED]: fetchOrganizationsFailed,

    [ActionTypes.REMOVE_ORGANIZATION_STATUS_MESSAGE]:
      removeOrganizationStatusMessage,

    [ActionTypes.FETCH_ORGANIZATIONS_SUCCESS]: storeOrganizations,
    [ActionTypes.FETCH_ORGANIZATIONS_NEXT_SUCCESS]: storeOrganizations,
    [ActionTypes.CREATE_ORGANIZATION_SUCCESS]: storeOrganizations,
    [ActionTypes.UPDATE_ORGANIZATION_SUCCESS]: storeOrganizations,

    [ActionTypes.DELETE_ORGANIZATION]: deleteOrganization,
    [ActionTypes.DELETE_ORGANIZATION_FAILED]: deleteOrganizationFailed,
    [ActionTypes.DELETE_ORGANIZATION_SUCCESS]: deleteOrganizationSuccess,

    [ActionTypes.FETCH_ORGANIZATION_CONTENT]: fetchOrganizationContent,
    [ActionTypes.FETCH_ORGANIZATION_CONTENT_FAILED]:
      fetchOrganizationContentFailed,
    [ActionTypes.FETCH_ORGANIZATION_CONTENT_SUCCESS]:
      fetchOrganizationContentSuccess,

    [ActionTypes.CREATE_ORGANIZATION_CONTENT]: createOrganizationContent,
    [ActionTypes.CREATE_ORGANIZATION_CONTENT_FAILED]:
      createOrganizationContentFailed,
    [ActionTypes.CREATE_ORGANIZATION_CONTENT_SUCCESS]:
      createOrganizationContentSuccess,

    [ActionTypes.FETCH_ORGANIZATION_ROLES]: fetchOrganizationRoles,
    [ActionTypes.FETCH_ORGANIZATION_ROLES_FAILED]: fetchOrganizationRolesFailed,
    [ActionTypes.FETCH_ORGANIZATION_ROLES_SUCCESS]:
      fetchOrganizationRolesSuccess,

    [ActionTypes.FETCH_ORGANIZATION_MEMBERSHIP]: fetchOrganizationMemberships,
    [ActionTypes.FETCH_ORGANIZATION_MEMBERSHIP_FAILED]:
      fetchOrganizationMembershipsFailed,
    [ActionTypes.FETCH_ORGANIZATION_MEMBERSHIP_SUCCESS]:
      storeOrganizationMemberships,

    [ActionTypes.CREATE_ORGANIZATION_MEMBERSHIP]: createOrganizationMembership,
    [ActionTypes.CREATE_ORGANIZATION_MEMBERSHIP_FAILED]:
      createOrganizationMembershipFailed,
    [ActionTypes.CREATE_ORGANIZATION_MEMBERSHIP_SUCCESS]:
      storeOrganizationMembership,

    [ActionTypes.UPDATE_ORGANIZATION_MEMBERSHIP]: updateOrganizationMembership,
    [ActionTypes.UPDATE_ORGANIZATION_MEMBERSHIP_FAILED]:
      updateOrganizationMembershipFailed,
    [ActionTypes.UPDATE_ORGANIZATION_MEMBERSHIP_SUCCESS]:
      storeOrganizationMembership,

    [ActionTypes.DELETE_ORGANIZATION_MEMBERSHIP]: deleteOrganizationMembership,
    [ActionTypes.DELETE_ORGANIZATION_MEMBERSHIP_FAILED]:
      deleteOrganizationMembershipFailed,
    [ActionTypes.DELETE_ORGANIZATION_MEMBERSHIP_SUCCESS]:
      deleteOrganizationMembershipSuccess,

    [ActionTypes.RESEND_MEMBER_INVITATION]: resendMemberInvitation,
    [ActionTypes.RESEND_MEMBER_INVITATION_FAILED]: resendMemberInvitationFailed,
    [ActionTypes.RESEND_MEMBER_INVITATION_SUCCESS]:
      resendMemberInvitationSuccess,
  },
};
